import { useState, useEffect, FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IRootState } from "@/redux/types";
import { getFilteredCourses } from "@/redux/actions/courses";
import { getCoursetypes } from "@/redux/actions/coursetypes";
import {
  createLearningRoute,
  editLearningRoute,
  getLearningRouteById,
  getLearningRoutes,
} from "@/redux/actions/learningRoutes";
import CourseSelectorModal from "./CourseSelectorModal";

interface Props {
  open: boolean;
  onClose: () => void;
  editingId?: number | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const CreateLearningRouteModal: FC<Props> = ({
  open,
  onClose,
  editingId,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation(["global"]);
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [courseIds, setCourseIds] = useState<string[]>([]);
  const [level, setLevel] = useState<"beginner" | "intermediate" | "high" | "">(
    ""
  );
  const [originalCourseIds, setOriginalCourseIds] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const { coursetypes } = useSelector((state: IRootState) => state.coursetype);
  const currentRoute = useSelector(
    (state: IRootState) => state.learningRoutes.currentLearningRoute
  );
  const [courseSelectorOpen, setCourseSelectorOpen] = useState(false);
  const handleOpenCourseSelector = () => setCourseSelectorOpen(true);
  const handleCloseCourseSelector = () => setCourseSelectorOpen(false);

  const handleCoursesSelected = (selected: string[]) => {
    setCourseIds(selected);
    handleCloseCourseSelector();
  };

  const isEditing = !!editingId;

  useEffect(() => {
    if (open && editingId) {
      dispatch(getLearningRouteById(editingId));
    }
  }, [open, editingId, dispatch]);

  useEffect(() => {
    if (!open) return;

    if (isEditing && currentRoute?.id === editingId) {
      const ids = currentRoute.courses?.map((c: any) => c.id.toString()) ?? [];

      setName(currentRoute.name);
      setDescription(currentRoute.description ?? "");
      setCourseIds(ids);
      setOriginalCourseIds(ids);
      setLevel(currentRoute.level);

      if (currentRoute.course_type_id) {
        setSelectedCategory(currentRoute.course_type_id.toString());
      }
    } else if (!isEditing) {
      setName("");
      setDescription("");
      setCourseIds([]);
      setLevel("");
      setSelectedCategory("");
    }
  }, [open, currentRoute, editingId, isEditing]);

  useEffect(() => {
    if (open) {
      if (coursetypes.length === 0) {
        dispatch(getCoursetypes());
      }
      const filter = {
        coursetype_id: selectedCategory ? Number(selectedCategory) : null,
        countries: [],
      };
      dispatch(getFilteredCourses(filter));
    }
  }, [open, selectedCategory, dispatch, coursetypes.length]);

  const handleSubmit = async () => {
    if (name.trim() && courseIds.length > 0 && level) {
      const newCourses = isEditing
        ? courseIds.filter((id) => !originalCourseIds.includes(id))
        : courseIds;

      const payload = {
        name,
        description,
        courseIds: newCourses,
        level,
        order: 0,
        status: true,
      };

      try {
        if (isEditing && editingId) {
          await dispatch(
            editLearningRoute(editingId, ...Object.values(payload))
          );
          onSuccess(t("route-updated-successfully"));
        } else {
          await dispatch(createLearningRoute(...Object.values(payload)));
          onSuccess(t("route-created-successfully"));
        }

        dispatch(getLearningRoutes());
        onClose();
      } catch {
        onError(t("error-saving"));
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditing ? t("edit-route") : t("create-learning-route")}
      </DialogTitle>
      <DialogContent>
        <Box component="form" display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label={t("name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel id="level-select-label">{t("difficulty")}</InputLabel>
              <Select
                labelId="level-select-label"
                value={level}
                label={t("difficulty")}
                onChange={(e) =>
                  setLevel(
                    e.target.value as "beginner" | "intermediate" | "high"
                  )
                }
              >
                <MenuItem value="">{t("select")}</MenuItem>
                <MenuItem value="beginner">
                  {t("difficulty-levels.beginner")}
                </MenuItem>
                <MenuItem value="intermediate">
                  {t("difficulty-levels.intermediate")}
                </MenuItem>
                <MenuItem value="high">{t("difficulty-levels.high")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label={t("description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Button variant="contained" onClick={handleOpenCourseSelector}>
            {t("select-courses")} ({courseIds.length})
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t("cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {isEditing ? t("update-route") : t("create")}
        </Button>
      </DialogActions>

      <CourseSelectorModal
        open={courseSelectorOpen}
        onClose={handleCloseCourseSelector}
        selectedCourseIds={courseIds}
        onSave={handleCoursesSelected}
      />
    </Dialog>
  );
};

export default CreateLearningRouteModal;
